import { useState, useCallback, useEffect } from "react";

export type ThemeSetting = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/**
 * 主题管理 Hook
 * 支持 light/dark/system 模式，并在 DOM 上设置对应的类名
 */
export function useTheme() {
  const [themeSetting, setThemeSetting] = useState<ThemeSetting>(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem("app-theme");
    return (stored as ThemeSetting) || "light";
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem("app-theme");
    if (stored === "light" || stored === "dark") return stored;

    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const applyTheme = useCallback((theme: ResolvedTheme) => {
    const root = document.documentElement;
    root.classList.remove("theme-vercel-light", "theme-vercel-dark", "dark");
    root.classList.add(`theme-vercel-${theme}`);
    if (theme === "dark") {
      root.classList.add("dark");
    }
  }, []);

  const handleThemeChange = useCallback(
    (newSetting: ThemeSetting) => {
      setThemeSetting(newSetting);
      const resolved =
        newSetting === "system"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          : newSetting;
      setResolvedTheme(resolved);
      applyTheme(resolved);
      localStorage.setItem("app-theme", newSetting);
    },
    [applyTheme]
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "app-theme" && e.newValue) {
        const newSetting = e.newValue as ThemeSetting;
        setThemeSetting(newSetting);
        const resolved =
          newSetting === "system"
            ? window.matchMedia("(prefers-color-scheme: dark)").matches
              ? "dark"
              : "light"
            : (newSetting as ResolvedTheme);
        setResolvedTheme(resolved);
        applyTheme(resolved);
      }
    };

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleMediaChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem("app-theme") === "system") {
        const newTheme = e.matches ? "dark" : "light";
        setResolvedTheme(newTheme);
        applyTheme(newTheme);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, [applyTheme]);

  return {
    themeSetting,
    resolvedTheme,
    setTheme: handleThemeChange,
  };
}
